import React, { useState, useCallback } from "react";
import Cropper from "react-easy-crop";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter } from "@/components/ui/dialog";
import { Loader2 } from "lucide-react";

function createImage(url) {
  return new Promise((resolve, reject) => {
    const img = new Image();
    img.crossOrigin = "anonymous";
    img.onload = () => resolve(img);
    img.onerror = reject;
    img.src = url;
  });
}

// Rimuove uno sfondo a tinta unita (es. logo su sfondo bianco): campiona il colore
// dai 4 angoli dell'immagine ritagliata e rende trasparenti i pixel simili, con
// una dissolvenza morbida sui bordi per evitare un taglio netto/frastagliato.
function stripFlatBackground(ctx, width, height, threshold = 38) {
  const imgData = ctx.getImageData(0, 0, width, height);
  const data = imgData.data;
  const corners = [[0, 0], [width - 1, 0], [0, height - 1], [width - 1, height - 1]];
  let r = 0, g = 0, b = 0;
  corners.forEach(([x, y]) => {
    const i = (y * width + x) * 4;
    r += data[i]; g += data[i + 1]; b += data[i + 2];
  });
  r /= 4; g /= 4; b /= 4;
  const soft = threshold * 1.6;
  for (let i = 0; i < data.length; i += 4) {
    const dr = data[i] - r, dg = data[i + 1] - g, db = data[i + 2] - b;
    const dist = Math.sqrt(dr * dr + dg * dg + db * db);
    if (dist < threshold) data[i + 3] = 0;
    else if (dist < soft) data[i + 3] = Math.round(((dist - threshold) / (soft - threshold)) * 255);
  }
  ctx.putImageData(imgData, 0, 0);
}

async function getCroppedImg(src, crop, format = "image/jpeg", removeBg = false) {
  const image = await createImage(src);
  const canvas = document.createElement("canvas");
  canvas.width = Math.round(crop.width);
  canvas.height = Math.round(crop.height);
  const ctx = canvas.getContext("2d");
  ctx.drawImage(image, crop.x, crop.y, crop.width, crop.height, 0, 0, crop.width, crop.height);
  if (removeBg) stripFlatBackground(ctx, canvas.width, canvas.height);
  const type = removeBg || format === "image/png" ? "image/png" : "image/jpeg";
  return new Promise((resolve) => canvas.toBlob((b) => resolve(b), type, 0.92));
}

const CropModal = ({ open, image, aspect = 1, format = "image/jpeg", allowBgRemoval = false, onCancel, onConfirm }) => {
  const [crop, setCrop] = useState({ x: 0, y: 0 });
  const [zoom, setZoom] = useState(1);
  const [pixels, setPixels] = useState(null);
  const [busy, setBusy] = useState(false);
  const [removeBg, setRemoveBg] = useState(false);
  const onComplete = useCallback((_, p) => setPixels(p), []);
  const confirm = async () => {
    if (!pixels) return;
    setBusy(true);
    const blob = await getCroppedImg(image, pixels, format, removeBg);
    setBusy(false);
    onConfirm(blob);
  };
  return (
    <Dialog open={open} onOpenChange={(o) => !o && onCancel()}>
      <DialogContent data-testid="crop-modal">
        <DialogHeader><DialogTitle>Ritaglia e metti a fuoco</DialogTitle></DialogHeader>
        <div className="relative w-full h-72 bg-black rounded-xl overflow-hidden">
          <Cropper image={image} crop={crop} zoom={zoom} aspect={aspect} onCropChange={setCrop} onZoomChange={setZoom} onCropComplete={onComplete} />
        </div>
        <div className="flex items-center gap-3 mt-3">
          <span className="text-sm text-muted-foreground">Zoom</span>
          <input type="range" min={1} max={3} step={0.01} value={zoom} onChange={(e) => setZoom(Number(e.target.value))} className="flex-1 accent-[#002FA7]" data-testid="crop-zoom" />
        </div>
        {allowBgRemoval && (
          <label className="flex items-center gap-2 mt-3 text-sm cursor-pointer" data-testid="crop-remove-bg-label">
            <input type="checkbox" checked={removeBg} onChange={(e) => setRemoveBg(e.target.checked)} className="w-4 h-4 accent-[#002FA7]" data-testid="crop-remove-bg" />
            Rimuovi sfondo automaticamente (per sfondi a tinta unita, es. bianco)
          </label>
        )}
        <DialogFooter>
          <button onClick={onCancel} className="rounded-full px-5 py-2 text-sm font-medium hover:bg-muted">Annulla</button>
          <button onClick={confirm} disabled={busy || !pixels} data-testid="crop-confirm" className="rounded-full bg-[#002FA7] text-white px-5 py-2 text-sm font-semibold hover:bg-[#00248a] disabled:opacity-60 flex items-center gap-2">{busy && <Loader2 className="w-4 h-4 animate-spin" />}Conferma</button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
};

export default CropModal;
